import { useEffect, useState } from 'react';

export function getSlideImageUrl(slide: SpApi.ISlideshowSlide): string | null {
    const firstItem = slide.items.find((item) => item.displayUrl !== null);

    return (
        firstItem?.displayUrl?.extraLarge3 ??
        firstItem?.displayUrl?.extraLarge2 ??
        firstItem?.displayUrl?.large ??
        firstItem?.displayUrl?.medium ??
        null
    );
}

/**
 * Preloads every slide's image into the browser cache ahead of playback, so a
 * slide's asset is already available by the time its transition begins — without
 * this, the incoming slide's <img> only starts fetching once it enters the DOM
 * (i.e. when the transition itself starts), which pops in mid-animation on a cold
 * cache (typically the first playthrough after a hard refresh).
 *
 * Fetches only — it deliberately does not call `img.decode()` to force the decode up front.
 * A decoded bitmap is width × height × 4 bytes (~36MB for a 3000×3000 slide, an order of
 * magnitude more than the encoded bytes) and `decode()` pins it, so decoding a whole slideshow
 * ahead of time could hold well over a gigabyte and exhaust iOS Safari's per-tab memory
 * (FOR-16489). The browser decodes each slide as its <img> paints instead.
 *
 * A slide with no image, or one whose image fails to load, is still marked ready
 * so a single broken asset can't stall playback indefinitely.
 */
export function useSlideshowImagePreload(slides: SpApi.ISlideshowSlide[]): ReadonlySet<number> {
    const [readyIndices, setReadyIndices] = useState<ReadonlySet<number>>(new Set());

    useEffect(() => {
        setReadyIndices(new Set());

        let cancelled = false;
        const images: HTMLImageElement[] = [];

        const markReady = (index: number) => {
            if (cancelled) {
                return;
            }

            setReadyIndices((prev) => {
                if (prev.has(index)) {
                    return prev;
                }

                const next = new Set(prev);
                next.add(index);
                return next;
            });
        };

        slides.forEach((slide, index) => {
            const url = getSlideImageUrl(slide);

            if (!url) {
                markReady(index);
                return;
            }

            const img = new Image();

            images.push(img);
            img.onload = () => markReady(index);
            img.onerror = () => markReady(index);
            img.src = url;

            if (img.complete) {
                markReady(index);
            }
        });

        return () => {
            cancelled = true;
            images.forEach((img) => {
                img.onload = null;
                img.onerror = null;
            });
        };
    }, [slides]);

    return readyIndices;
}

/**
 * How many leading slides must have preloaded before a slideshow counts as ready to start
 * playing. Matches `SlideshowPlayerTicker`'s own `INITIAL_BUFFER_SLIDE_COUNT`, so by the time a
 * caller reveals the slideshow the ticker's identical internal gate is already satisfied from
 * cache and playback starts on the first frame instead of showing its buffering state.
 */
export const INITIAL_BUFFER_SLIDE_COUNT = 5;

/**
 * Whether the leading `INITIAL_BUFFER_SLIDE_COUNT` slides (or all of them, for a shorter
 * slideshow) have finished preloading. False for an empty slideshow, which has nothing to play.
 */
export function hasBufferedInitialSlides(
    readyIndices: ReadonlySet<number>,
    slideCount: number
): boolean {
    const target = Math.min(INITIAL_BUFFER_SLIDE_COUNT, slideCount);

    for (let i = 0; i < target; i++) {
        if (!readyIndices.has(i)) {
            return false;
        }
    }

    return slideCount > 0;
}
